import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, Card, DialogActions, DialogContent, DialogContentText, DialogTitle, Skeleton, Typography} from "@mui/material";
import LazyImage from "../../../../../../components/app-specific/lazy-image";
import {useBizFileUrlsResolver} from "../../../../../../hooks/use-biz-file-urls-resolver";
import {ReactComponent as ImagePlaceholder} from "../../../../../../../assets/images/dropzone/image-placeholder.svg";
import DateUtils from "../../../../../../../core/services/date-utils";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import Button from "../../../../../../components/base/button/button";
import Dialog from "../../../../../../components/base/dialog";
import {EditOutlined, OpenInNew} from "@mui/icons-material";
import IconButton from "../../../../../../components/base/button/icon_button";
import Api from "../../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import CustomerAutocomplete from "../../../../../../components/text-fields/autocomplete/customer-autocomplete";
import {Link} from "react-router-dom";
import Utils from "../../../../../../../core/services/utils/utils";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";


const initialUpsertConnectionDialogState = {
    open: false,
    customer: null,
    partnerId: null,
    onClose: () => void 0,
    onConnectionUpdated: () => void 0,
}

const initialCreateConnectionDialogState = {
    open: false,
    partnerId: null,
    onClose: () => void 0,
    onConnectionCreated: () => void 0,
}

const dataGridSaveConfig = {
    name: 'PartnershipViewBuyersDataGrid',
    version: 1,
}

const dataGridColumnNames = {
    avatar: 'avatar',
    name: 'name',
    connectedOn: 'connectedOn',
    website: 'website',
    relatedCustomer: 'relatedCustomer',
    importedProductsCount: 'importedProductsCount',
}

const PartnershipViewBuyers = () => {
    const [_partners, setPartners] = useState([]);
    const [_requests, setRequests] = useState([]);
    const [upsertConnectionDialogState, setUpsertConnectionDialogState] = useState(initialUpsertConnectionDialogState);
    const [createConnectionDialogState, setCreateConnectionDialogState] = useState(initialCreateConnectionDialogState);
    const [loadingPartners, setLoadingPartners] = useState(true);
    const [loadingRequests, setLoadingRequests] = useState(true);
    const isMounted = useIsMounted();
    const partnerInformation = usePartnerInformation();
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);
    // const dispatch = useDispatch();

    const [partners, requests] = useMemo(() => {
        const getPartnerLogoUrl = (logo) => (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? "").concat(logo);
        const partners = _partners.map(e => ({
            ...e,
            buyerInfo: e.buyerInfo ? {
                ...e.buyerInfo,
                logo: getPartnerLogoUrl(e.buyerInfo.logo),
            } : undefined,
        }))
        const requests = _requests.map(e => ({
            ...e,
            buyerInfo: e.buyerInfo ? {
                ...e.buyerInfo,
                logo: getPartnerLogoUrl(e.buyerInfo.logo),
            } : undefined,
        }))
        return [partners, requests];
    }, [_partners, _requests, partnerInformation?.configuration?.PartnerLogoUrlBaseAddress])


    /**
     * Fetches the list of partners.
     * @type {(function(): Promise<void>)}
     */
    const getPartners = useCallback(async () => {
        setLoadingPartners(true);
        setLoadingRequests(true);
        const response = await Api.partnerConnectionBuyersList();
        if (!isMounted())
            return;
        setLoadingPartners(false);
        setLoadingRequests(false);
        if (response?.resultFlag) {
            setPartners(response.data.filter(e => e.hasApproved));
            setRequests(response.data.filter(e => !e.hasApproved))
        }
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - Fetches the list of requests.
     */
    useEffect(() => {
        void getPartners();
    }, [getPartners])

    // /**
    //  * Fetches the requests made for the buyer partners of this view.
    //  * @type {(function(): Promise<void>)|*}
    //  */
    // const getPartnerRequests = useCallback(async () => {
    //     setLoadingRequests(true);
    //     const response = {
    //         resultFlag: true,
    //         data: Array.from({length: 10}, (_, i) => ({
    //             id: `10000000-0000-0000-0000-00000000000${i + 1}`,
    //             name: `Buyer ${i + 1}`,
    //             imageToken: `10000000-0000-0000-0000-00000000000${i + 1}`,
    //             extension: `png`,
    //             description: `This is the description for the description of the buyer that wants us to be in a way that i am trying to be the actual reason that they become the buy that they are now.`,
    //             requestedDateTime: DateUtils.serverNow().subtract(i, 'days').format(DateUtils.withoutOffsetFormat),
    //         })),
    //     }
    //     if (!isMounted())
    //         return;
    //     setLoadingRequests(false);
    //     if (response?.resultFlag) {
    //         setRequests(response?.data ?? []);
    //     }
    // }, [isMounted])
    //
    // /**
    //  * As soon as the component mounts:
    //  * - fetches the requests made for the buyer partners of this view
    //  */
    // useEffect(() => {
    //     void getPartnerRequests();
    // }, [getPartnerRequests])

    /**
     * Opens the create connection dialog.
     * @type {(function(*): void)}
     */
    const openCreateConnectionDialog = useCallback((request) => {
        setCreateConnectionDialogState({
            onConnectionCreated: (customer) => {
                setRequests(prevState => prevState.filter(e => e.id !== request.id))
                setPartners(prevState => [
                    ...prevState,
                    {
                        ...request,
                        hasApproved: true,
                        acceptDateTime: DateUtils.serverNow(),
                        customer: {
                            id: customer.id,
                            name: customer.name,
                        },
                    }
                ])
            },
            open: true,
            partnerId: request.id,
            onClose: () => setCreateConnectionDialogState(initialCreateConnectionDialogState),
        })
    }, [])

    /**
     * Opens the create connection dialog.
     * @type {(function(*): void)}
     */
    const openUpdateConnectionDialog = useCallback((partner) => {
        setUpsertConnectionDialogState({
            onConnectionUpdated: (customer) => {
                setPartners(prevState => prevState.map(e => e.id === partner.id
                    ? {
                        ...e,
                        customer: {id: customer.id, name: customer.name,},
                    }
                    : e
                ))
            },
            open: true,
            partnerId: partner.id,
            customer: partner.customer,
            onClose: () => {
                setUpsertConnectionDialogState(initialUpsertConnectionDialogState)
            },
        })
    }, [])

    // /**
    //  * Opens the upsert connection dialog.
    //  * @type {(function(*, *))}
    //  */
    // const cancelRequest = useCallback((request, setLoading) => {
    //     dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
    //         open: true,
    //         data: null,
    //         removedEntryTitle: `The Request from "${request.name}"`,
    //         onClose: async (confirmed) => {
    //             if (!confirmed)
    //                 return;
    //             setLoading?.(true);
    //             // TODO: call api to cancel a buyer request.
    //             const response = {
    //                 resultFlag: true,
    //             }
    //             if (!isMounted())
    //                 return false
    //             setLoading?.(false);
    //             if (response?.resultFlag) {
    //                 setRequests(prevState => prevState.filter(e => e.id !== request.id));
    //             }
    //             return response?.resultFlag ?? false
    //         }
    //     }));
    // }, [dispatch, isMounted])


    const columns = useMemo(() =>
        /**@type {DataGridColumn[]} */
        [
            {
                name: dataGridColumnNames.avatar,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                title: "Name",
                name: dataGridColumnNames.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
            },
            {
                title: "Connected On",
                name: dataGridColumnNames.connectedOn,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
            },
            {
                title: "Website",
                name: dataGridColumnNames.website,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: {minWidth: 200, size: 200},
            },
            {
                title: "Related Customer",
                name: dataGridColumnNames.relatedCustomer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: {minWidth: 200, size: 200},
            },
            // {
            //     title: "Imported Products Count",
            //     name: dataGridColumnNames.importedProductsCount,
            //     alignment: DataGridColumnAlignments.left,
            //     type: DataGridColumnTypes.string,
            //     sortable: false,
            //     width: {minWidth: 200, size: 200},
            // },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return partners?.map(partner => ({
            key: partner?.id,
            data: partner,
            cells: {
                [dataGridColumnNames.avatar]:
                    <div className={'operator-image-profile-list'}>
                        {
                            <LazyImage
                                alt={'profile'}
                                noImage={<ImagePlaceholder style={{height: 50, width: 50, borderRadius: 8}}/>}
                                src={bizFileUrls.getFilePreviewUrl(partner.buyerInfo.logo)}
                                height={50}
                                style={{height: 50, width: 50, borderRadius: 8}}
                            />
                        }
                    </div>,
                [dataGridColumnNames.name]: partner?.buyerInfo?.name,
                [dataGridColumnNames.connectedOn]: partner?.acceptDateTime,
                [dataGridColumnNames.website]:
                    <>
                        <IconButton
                            className={'mr-2'}
                            color={'secondary'}
                            onPressed={() => window.open(partner.buyerInfo.website, '_blank')}>
                            <OpenInNew className={'icon small'}/>
                        </IconButton>
                        <a href={partner?.buyerInfo?.website}
                           target={'_blank'}
                           rel="noreferrer"
                           className={'text-sm text-color-1'}
                           {...dataGridCellTextProps}
                        >
                            {Utils.formatLinkUrlDisplay(partner?.buyerInfo?.website)}
                        </a>
                    </>,
                [dataGridColumnNames.relatedCustomer]:
                    <>
                        <IconButton
                            className={'mr-2'}
                            color={'secondary'}
                            onPressed={() => openUpdateConnectionDialog(partner)}>
                            <EditOutlined className={'icon small'}/>
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'text-sm'}>
                            {partner?.customer?.name}
                        </p>
                        <IconButton
                            className={'ml-2'}
                            color={'secondary'}
                            component={Link}
                            to={routeFunctions.customer.generalInformation.profile(partner?.customer?.id)}
                        >
                            <OpenInNew className={'icon small'}/>
                        </IconButton>
                    </>,
                // [dataGridColumnNames.importedProductsCount]: partner?.importedProductsCount,

            }
        })) ?? []
    }, [openUpdateConnectionDialog, partners])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center',}}>
                <Typography variant="body2" sx={{marginBottom: 2, textAlign: 'start', width: '100%'}}>
                    This section Lists the partners that have access to your inventory and public parts. They act as one of your customers
                    and can purchase from you.
                </Typography>
                {
                    (loadingRequests || !!requests.length) &&
                    <Card sx={{
                        padding: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: 'background.tertiary',
                        width: '100%',
                    }}>
                        <Typography variant="body1" fontWeight={600} sx={{marginBottom: 1}}>
                            Requests
                        </Typography>
                        {
                            loadingRequests &&
                            Array.from({length: 5}, (_, i) => (
                                <Skeleton
                                    key={`loading-${i}`}
                                    variant="rectangular"
                                    width={'100%'}
                                    sx={(theme) => ({minHeight: 120, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                />
                            ))
                        }
                        {
                            !loadingRequests && !!requests.length &&
                            requests.map((request) => (
                                <Card
                                    key={request.id}
                                    variant={'outlined'}
                                    sx={{padding: 1, display: 'flex', alignItems: 'center', gap: 1}}
                                >
                                    <LazyImage
                                        alt={'profile'}
                                        noImage={<ImagePlaceholder style={{height: 100, width: 100, borderRadius: 8}}/>}
                                        src={bizFileUrls.getFilePreviewUrl(request.buyerInfo.logo)}
                                        height={100}
                                        style={{height: 100, width: 100, borderRadius: 8}}
                                    />
                                    <Box
                                        sx={{display: 'flex', flexDirection: 'column', gap: 1, marginLeft: 2, flexGrow: 1, marginRight: 1}}>
                                        <Typography variant={'body2'} fontWeight={600}>
                                            {request.buyerInfo.name ?? ""}
                                        </Typography>
                                        <Typography variant={'caption'}>
                                            {request.buyerInfo.description ?? ""}
                                        </Typography>
                                        <Typography variant={'caption'} color={'text.secondary'}>
                                            {DateUtils.serverToLocal(request.requestedDateTime).format(DateUtils.dateTimeFormat)}
                                        </Typography>
                                    </Box>
                                    <Button variant={'outlined'} onClick={() => openCreateConnectionDialog(request)}>
                                        Accept Request
                                    </Button>
                                </Card>
                            ))
                        }
                    </Card>
                }
                <Typography variant="body1" fontWeight={600} sx={{marginBlockStart: 4, width: '100%', textAlign: 'start'}}>
                    Current Buyers
                </Typography>
                <PartnerDataGrid
                    classNames={{
                        toolbar: "pt-3"
                    }}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    hideFooterActions={true}
                    state={{
                        loading: {state: loadingPartners},
                        density: dataGridDensity,
                    }}
                    hideExporting
                />
            </Box>
            <CreateConnectionDialog
                open={createConnectionDialogState.open}
                onClose={createConnectionDialogState.onClose}
                connectionRequestId={createConnectionDialogState.partnerId}
                onSuccess={createConnectionDialogState.onConnectionCreated}
            />
            <UpdateConnectionEntityDialogState
                open={upsertConnectionDialogState.open}
                onClose={upsertConnectionDialogState.onClose}
                customer={upsertConnectionDialogState.customer}
                connectionId={upsertConnectionDialogState.partnerId}
                onConnectionUpdated={upsertConnectionDialogState.onConnectionUpdated}
            />
        </>
    );
}

const CreateConnectionDialog = ({open, onClose, connectionRequestId, onSuccess}) => {
    const [submitting, setSubmitting] = useState(false);
    const [customer, setCustomer] = useState();
    const isMounted = useIsMounted();

    /**
     * As soon as the dialog opens:
     * - resets the state values
     */
    useEffect(() => {
        if (open) {
            setSubmitting(false);
            setCustomer(null);
        }
    }, [open]);


    /**
     * Accepts and connects the customer as the buyer of the current partner.
     * @type {(function(): Promise<void>)}
     */
    const acceptAndConnect = useCallback(async () => {
        setSubmitting(true);
        const response = await Api.partnerConnectionAcceptSellerRequest({
            customerId: customer?.id,
            partnerConnectionId: connectionRequestId,
        })
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess(customer);
            onClose();
        }
    }, [connectionRequestId, customer, isMounted, onClose, onSuccess])

    return (
        <>
            <Dialog open={open} setOpen={onClose}>
                <DialogTitle fontWeight={600}>
                    Accept Buyer Request
                </DialogTitle>
                <DialogContent className={'d-flex flex-column py-3'}>
                    <DialogContentText variant={'body2'} sx={{marginBlockEnd: 2}}>
                        <span className={'font-weight-semi-bold mr-2'}>Assign Customer</span>
                        Each buyer partner is also a customer in your system and will do the purchasing through the assigned customer.
                    </DialogContentText>
                    <CustomerAutocomplete
                        label={'Customer'}
                        fullWidth
                        disabled={submitting}
                        value={customer}
                        onChange={(_, customer) => setCustomer(customer)}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'button'}
                        onClick={() => onClose()}
                        borderRadius={'0 10px 0 10px '}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={submitting || !customer}
                        onClick={acceptAndConnect}
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            submitting
                                ? "Connecting..."
                                : "Accept and Connect"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const UpdateConnectionEntityDialogState = ({open, onClose, customer, connectionId, onConnectionUpdated}) => {
    const [replacement, setReplacement] = useState();
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted()

    /**
     * As soon as the dialog opens:
     * - resets the state values
     */
    useEffect(() => {
        if (open) {
            setSubmitting(false);
            setReplacement(null);
        }
    }, [open]);

    /**
     * Replaces the customer of the provided connection with the replacement entity.
     * @type {(function(): Promise<void>)}
     */
    const replaceCustomer = useCallback(async () => {
        const forApi = {
            customerId: replacement?.id,
            partnerConnectionId: connectionId
        }
        setSubmitting(true);
        const response = await Api.partnerConnectionReplaceCustomer(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onConnectionUpdated(replacement);
            onClose();
        }
    }, [connectionId, isMounted, onClose, onConnectionUpdated, replacement])

    return (
        <>
            <Dialog open={open} setOpen={onClose}>
                <DialogTitle fontWeight={600}>
                    Change Assigned Customer
                </DialogTitle>
                <DialogContent className={'d-flex flex-column py-3'}>
                    <DialogContentText variant={'body2'} sx={{marginBlockEnd: 2}}> By changing the related customer, the partner will use
                        the newly assigned customer but the old one remains in your
                        system and you can assign the partner to the old customer again whenever you want.
                    </DialogContentText>
                    <div className={'d-flex align-items-center mb-3'} style={{gap: 12}}>
                        <p className={'text-sm'}>
                            Current Customer:
                        </p>
                        <p className={'text-sm font-weight-semi-bold tertiary-main'}>
                            {customer?.name ?? ""}
                        </p>
                        <IconButton
                            color={'paper.secondary'}
                            disabled={submitting}
                            component={Link}
                            to={routeFunctions.customer.generalInformation.profile(customer?.id ?? 0)}
                        >
                            <OpenInNew className='icon normal'/>
                        </IconButton>
                    </div>
                    <CustomerAutocomplete
                        label={'Replacement Customer'}
                        fullWidth
                        disabled={submitting}
                        value={replacement}
                        onChange={(_, replacement) => setReplacement(replacement)}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'button'}
                        onClick={() => onClose()}
                        borderRadius={'0 10px 0 10px '}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={submitting || !customer}
                        onClick={replaceCustomer}
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            submitting
                                ? "Applying..."
                                : "Change Customer"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PartnershipViewBuyers;
